import { isAxiosError } from "axios";
import { create } from "zustand";
import { ClientApp } from "target_wasm";
import { axiosInstance } from "contexts/axiosApiInstance";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import { setImportQueueModel } from "@/components/canvas/fileUpload/importQueue";
import {
  createWebsocket,
  type DisconnectReason,
} from "@/components/canvas/utils/CanvasActions";
import { cleanup } from "@/components/canvas/utils/utils";
import { SHELL_PAGES } from "@/lib/navigation/pages";

export type Refusal =
  "not_found" | "duplicate" | "signed_out" | "updating" | "failed";
export type OpenResult = "ready" | "cancelled" | Refusal;

interface SocketHandlers {
  onConnected: () => void;
  onDisconnected: (reason: DisconnectReason) => void;
}

interface Session {
  modelId: string;
  clientId: string;
  clientApp: ClientApp;
  ready: boolean;
  result: Promise<OpenResult>;
  settle: (result: OpenResult) => void;
  disconnect: () => void;
  stopWatching: () => void;
  onDuplicate: () => void;
  retry: ReturnType<typeof setTimeout> | null;
}

// One model is open per tab: the one being opened, then the one on the canvas.
let session: Session | null = null;

/** The model being opened, until the canvas shows it or it closes; its
 *  Overview row shows the spinner meanwhile. */
export const useOpeningModel = create<{ modelId: string | null }>(() => ({
  modelId: null,
}));

export type RolloutPhase = "idle" | "updating" | "recovered";

/** `updating`: a release is rolling out — the mesh is swapping, or this bundle
 *  is the old one and the tab waits for the new one to be served. `recovered`:
 *  the tab just came back on the new bundle; the Overview says models are
 *  available, for a moment. It opens nothing by itself. */
export const useRollout = create<{ phase: RolloutPhase }>(() => ({
  phase: "idle",
}));

const RETRY_WHILE_UPDATING_MS = 5000;
const NEW_BUILD_POLL_MS = 5000;
const RECOVERED_BANNER_MS = 15000;
/** The entry bundle the previous document ran when it left for a newer one.
 *  The next document reads it to tell a navigation that landed on a pod still
 *  serving the old build from one that landed on the new one. */
const LEFT_BUNDLE_KEY = "mycorr.rollout.left-bundle";
/** A watch that outlives the rollout polls for nothing; 30 min covers a sync
 *  that stalls and is then rolled forward. */
const MAX_WATCH_TICKS = 360;
let watch: ReturnType<typeof setTimeout> | null = null;
let recoveredTimer: ReturnType<typeof setTimeout> | null = null;

/** Loads the model with no canvas on the page; resolves once it is ready to
 *  show. Opening the model that is already open joins it. */
export function openModel(
  modelId: string,
  onDuplicate: () => void = () => {},
): Promise<OpenResult> {
  if (session?.modelId === modelId) {
    session.onDuplicate = onDuplicate;
    useOpeningModel.setState({ modelId: null });
    return session.result;
  }
  closeModel();
  if (useRollout.getState().phase === "recovered") {
    useRollout.setState({ phase: "idle" });
  }

  const clientId = localStorage.getItem("clientID");
  if (!clientId) return Promise.resolve("signed_out");

  const store = useBoundStore.getState();
  store.initializeWorker();
  const clientApp = ClientApp.new(clientId, modelId);
  store.setClientApp(clientApp);
  // A held import batch needs this worker and this client to run.
  setImportQueueModel(modelId);

  const { promise: result, resolve: settle } =
    Promise.withResolvers<OpenResult>();
  const s: Session = {
    modelId,
    clientId,
    clientApp,
    ready: false,
    result,
    settle,
    disconnect: () => {},
    stopWatching: () => {},
    onDuplicate,
    retry: null,
  };
  session = s;
  useOpeningModel.setState({ modelId });

  // The store updates from inside wasm calls, where the client is still
  // borrowed; asking it anything before the call returns throws.
  s.stopWatching = useBoundStore.subscribe(() =>
    queueMicrotask(() => {
      if (
        session !== s ||
        s.ready ||
        !s.clientApp.is_model_ready() ||
        useBoundStore.getState().websocket?.readyState !== WebSocket.OPEN
      )
        return;
      s.ready = true;
      s.stopWatching();
      stopWatchingForBundle();
      useRollout.setState({ phase: "idle" });
      s.settle("ready");
    }),
  );
  const handlers: SocketHandlers = {
    onConnected: () => {},
    onDisconnected: (reason) => {
      if (session !== s) return;
      if (s.ready) {
        if (reason === "duplicate") s.onDuplicate();
        return;
      }
      // Bounced over the version. A newer server: this bundle is the old one,
      // so no attempt succeeds until the new one is served. An older server —
      // an old edge still in rotation after this tab already reloaded, or a
      // rollback — is retried like a hub-less edge, while a watch catches the
      // rollback's bundle.
      let bounced = false;
      let behind = false;
      try {
        bounced = s.clientApp.is_new_version_pending();
        behind = s.clientApp.is_server_behind();
      } catch {
        // WASM not ready — then it was not a version bounce.
      }
      if (bounced) {
        reloadWhenNewBuildIsServed();
        return;
      }
      if (behind) watchForNewBundle();
      // A browser hides why an upgrade failed; the server says it here.
      const refusal = behind
        ? Promise.resolve<Refusal>("updating")
        : reason === "duplicate"
          ? Promise.resolve<Refusal>("duplicate")
          : explainRefusal(modelId, clientId);
      void refusal.then((r) => {
        if (session !== s) return;
        if (r === "updating") {
          // The row keeps spinning and the Overview banner says why; the next
          // attempt lands on an edge of the new release once it is in rotation.
          useRollout.setState({ phase: "updating" });
          s.retry = setTimeout(() => {
            s.retry = null;
            if (session !== s) return;
            try {
              s.clientApp.clear_version_bounce();
            } catch {
              // WASM not ready — nothing recorded to clear.
            }
            s.disconnect();
            s.disconnect = connect(s, handlers);
          }, RETRY_WHILE_UPDATING_MS);
          return;
        }
        s.settle(r);
        closeModel();
      });
    },
  };
  s.disconnect = connect(s, handlers);
  return result;
}

const entryBundle = (doc: Document) =>
  doc
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.getAttribute("src") ?? null;

const overviewUrl = () =>
  new URL(
    SHELL_PAGES.Overview.path.replace(/^\//, ""),
    new URL(import.meta.env.BASE_URL, location.origin),
  ).toString();

/** Poll `index.html` until the client tier serves an entry bundle other than
 *  the running one, then leave for the Overview as a navigation — not
 *  `location.reload()`, which Firefox has been seen to bring up blank after a
 *  page that polled (docs/RolloutRefreshFindings.md § 6). Never while a model
 *  is shown: its edits may be unsaved, and the edge that bounces it hands it
 *  to `DeployReloadGate`, which holds for the save. The bundle left is
 *  recorded so the next document can tell whether it landed on the new build.
 *  Idempotent; stops when a model is ready or after `MAX_WATCH_TICKS`. */
export function watchForNewBundle() {
  if (watch) return;
  const running = entryBundle(document);
  let ticks = 0;
  const tick = async () => {
    const served = await fetch(`${import.meta.env.BASE_URL}index.html`, {
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.text() : null))
      .then(
        (html) =>
          html &&
          entryBundle(new DOMParser().parseFromString(html, "text/html")),
      )
      .catch(() => null);
    if (!watch) return;
    if (served && served !== running && !session?.ready) {
      try {
        sessionStorage.setItem(LEFT_BUNDLE_KEY, running ?? "");
      } catch {
        // No storage: the next document shows nothing, which is still correct.
      }
      window.location.assign(overviewUrl());
      return;
    }
    if (++ticks >= MAX_WATCH_TICKS) {
      stopWatchingForBundle();
      useRollout.setState({ phase: "idle" });
      return;
    }
    watch = setTimeout(() => void tick(), NEW_BUILD_POLL_MS);
  };
  watch = setTimeout(() => void tick(), 0);
}

function stopWatchingForBundle() {
  if (watch) clearTimeout(watch);
  watch = null;
}

/** The server closed the socket over a newer release (`ForceRefresh`). The
 *  client pods roll after the mesh, so reloading at once fetched this same
 *  bundle and was bounced again (v0.199.1: 11 minutes of reloads). Close the
 *  model — the Overview still works on this bundle — and leave once a
 *  different bundle is served. */
export function reloadWhenNewBuildIsServed() {
  closeModel();
  useRollout.setState({ phase: "updating" });
  watchForNewBundle();
}

/** On the Overview's mount: the document that left for a newer bundle wrote
 *  which one it ran. Same bundle here — the navigation was balanced to a pod
 *  still serving the old build (the client wave keeps both for a minute) —
 *  so keep waiting. A different one: say models are back, briefly. */
export function resumeRollout() {
  let left: string | null;
  try {
    left = sessionStorage.getItem(LEFT_BUNDLE_KEY);
  } catch {
    return;
  }
  if (left === null) return;
  const running = entryBundle(document);
  if (running !== null && left === running) {
    useRollout.setState({ phase: "updating" });
    watchForNewBundle();
    return;
  }
  try {
    sessionStorage.removeItem(LEFT_BUNDLE_KEY);
  } catch {
    // Already read; a stale record only repeats the banner once.
  }
  useRollout.setState({ phase: "recovered" });
  if (recoveredTimer) clearTimeout(recoveredTimer);
  recoveredTimer = setTimeout(() => {
    if (useRollout.getState().phase === "recovered") {
      useRollout.setState({ phase: "idle" });
    }
  }, RECOVERED_BANNER_MS);
}

/** A new socket for the open model; the reconnect engine's attempts land here. */
export function reconnectModel(handlers: SocketHandlers): boolean {
  if (!session) return false;
  session.disconnect();
  session.disconnect = connect(session, handlers);
  return true;
}

/** Closes the model if it is still being opened, e.g. its row was cancelled. */
export function cancelOpening(modelId: string) {
  if (session?.modelId === modelId && !session.ready) {
    useRollout.setState({ phase: watch ? "updating" : "idle" });
    closeModel();
  }
}

export function closeModel() {
  const s = session;
  if (!s) return;
  session = null;
  useOpeningModel.setState({ modelId: null });
  if (s.retry) clearTimeout(s.retry);
  s.stopWatching();
  s.disconnect();
  s.settle("cancelled");
  const store = useBoundStore.getState();
  store.resetPresence();
  // `resetClientApp` clears the canvas mode and every overlay the freed
  // ClientApp pushed — they're per-model and would otherwise stick.
  cleanup(() => store.resetClientApp(), "Reset client app");
  // Before the worker goes: a row that starts between the two would post
  // to a worker about to be terminated.
  setImportQueueModel(null);
  cleanup(() => store.terminateWorker(), "Terminate worker");
}

export function notifyRefusal(refusal: Refusal) {
  if (refusal === "not_found") {
    showNotificationGlobal("Model was not found!", "error");
  } else if (refusal === "duplicate") {
    showNotificationGlobal(
      "This model is already active in a different tab or window!",
    );
  } else if (refusal === "failed") {
    showNotificationGlobal("Couldn't open the model. Try again.", "error");
  }
  // signed_out: the session-expired dialog already says so.
}

function connect(s: Session, handlers: SocketHandlers) {
  return createWebsocket({
    clientId: s.clientId,
    clientApp: s.clientApp,
    modelId: s.modelId,
    onSocketChange: useBoundStore.getState().setWebsocket,
    ...handlers,
  });
}

async function explainRefusal(
  modelId: string,
  clientId: string,
): Promise<Refusal> {
  const [access, connectable] = await Promise.allSettled([
    axiosInstance.post(`/action/check_access/${modelId}`),
    axiosInstance.get(`/action/can_connect/${modelId}/${clientId}`),
  ]);
  const status = (r: PromiseSettledResult<unknown>) =>
    r.status === "rejected" && isAxiosError(r.reason)
      ? r.reason.response?.status
      : undefined;
  if (status(access) === 401 || status(connectable) === 401) {
    return "signed_out";
  }
  if (status(access) === 403 || status(access) === 404) return "not_found";
  if (status(connectable) === 409) return "duplicate";
  // 503: the edge's release has no hub (mid-rollout). 502: the load balancer
  // has no healthy edge at all. Both pass.
  const edge = status(connectable);
  if (edge === 502 || edge === 503) return "updating";
  return "failed";
}
